import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Fade,
  Paper,
  Chip,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha,
  Tooltip,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputBase,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DownloadIcon from "@mui/icons-material/Download";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TuneIcon from "@mui/icons-material/Tune";
import KeyIcon from "@mui/icons-material/Key";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LanguageIcon from "@mui/icons-material/Language";
import LinkIcon from "@mui/icons-material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import config from "../../../config";

const DualRangeInput = ({ minVal, maxVal, onMinChange, onMaxChange, disabled }) => {
  const spinCSS = {
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { display: 'none', WebkitAppearance: 'none', margin: 0 },
    '& input[type=number]': { MozAppearance: 'textfield' }
  };
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      bgcolor: disabled ? 'transparent' : 'background.paper',
      border: '1px solid',
      borderColor: disabled ? 'transparent' : 'divider',
      borderRadius: 1.5,
      overflow: 'hidden',
      width: 'max-content',
      mx: 'auto'
    }}>
      <InputBase
        type="number"
        placeholder="Min"
        value={minVal}
        onChange={(e) => onMinChange(e.target.value)}
        disabled={disabled}
        inputProps={{ style: { textAlign: 'center', fontSize: '0.875rem', padding: '6px 4px', width: '55px' } }}
        sx={spinCSS}
      />
      <Box sx={{ width: '1px', height: 20, bgcolor: disabled ? 'transparent' : 'divider' }} />
      <InputBase
        type="number"
        placeholder="Max"
        value={maxVal}
        onChange={(e) => onMaxChange(e.target.value)}
        disabled={disabled}
        inputProps={{ style: { textAlign: 'center', fontSize: '0.875rem', padding: '6px 4px', width: '55px' } }}
        sx={spinCSS}
      />
    </Box>
  );
};

const POSConfigure = () => {
  const { store_id: route_store_id } = useParams();
  const location = useLocation();
  const isEditMode = location.pathname.includes('/edit');
  const navigate = useNavigate();
  const theme = useTheme();

  // Generic states
  const [loading, setLoading] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Store/POS Data
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [posSystems, setPosSystems] = useState([]);
  const [posSystemsLoading, setPosSystemsLoading] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    selectedStoreId: "",
    posSystem: "",
    storeDomain: "",
    consumerKey: "",
    consumerSecret: ""
  });

  const [isEditingWpCreds, setIsEditingWpCreds] = useState(false);

  const [manualFile, setManualFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Flow State
  const [activeStep, setActiveStep] = useState(0);

  // Mapping State (Step 2)
  const [analyzing, setAnalyzing] = useState(false);
  const [mappingSuggestions, setMappingSuggestions] = useState([]);
  const [targetAttributes, setTargetAttributes] = useState([]);
  const [requiredMissing, setRequiredMissing] = useState([]);
  const [confirmedMappings, setConfirmedMappings] = useState([]);
  const [sampleData, setSampleData] = useState([]);
  const [requiredMissingHints, setRequiredMissingHints] = useState([]);
  const [posIdDupeWarning, setPosIdDupeWarning] = useState(null);
  const [colStats, setColStats] = useState({});
  const [shopifyAutoFields, setShopifyAutoFields] = useState([]);
  const [wordpressTestStatus, setWordpressTestStatus] = useState("untested"); // untested, testing, success, error
  const [wordpressTestError, setWordpressTestError] = useState("");
  // WP Field Discovery
  const [wpDiscovery, setWpDiscovery] = useState(null); // { product_keys, variation_keys, meta_keys, target_attributes }
  const [wpMappings, setWpMappings] = useState([]); // flat confirmed mappings list across all three sections
  const [wpMappingError, setWpMappingError] = useState(null);

  // Step 4: Filter Configuration States
  const [isGeneratingIcons, setIsGeneratingIcons] = useState(false);
  const [categoryConfig, setCategoryConfig] = useState({});
  const [moodConfig, setMoodConfig] = useState({});
  const [budgetRanges, setBudgetRanges] = useState([
    { display_text: "No preference", min: 0, max: 9999 },
    { display_text: "$0-$25", min: 0, max: 25 },
    { display_text: "$25-$50", min: 25, max: 50 },
    { display_text: "$50-$75", min: 50, max: 75 },
    { display_text: "$75+", min: 75, max: 9999 }
  ]);
  const [potencyFilterEnabled, setPotencyFilterEnabled] = useState(false);
  const [locationFilterEnabled, setLocationFilterEnabled] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [allMoods, setAllMoods] = useState([]);
  const [locationLimitDialog, setLocationLimitDialog] = useState({ open: false, found: 0, allowed: 0, locations: [] });

  useEffect(() => {
    if (isEditMode && route_store_id) {
      fetchEditConfig();
    } else {
      loadStores();
      loadPosSystems();
    }
  }, [isEditMode, route_store_id]);

  const fetchEditConfig = async () => {
    setIsHydrating(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/client/pos/edit/config/${route_store_id}`, {
        headers: config.getHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setFormData({
          selectedStoreId: route_store_id,
          storeName: data.data.store_name || "",
          posSystem: data.data.pos_system,
          storeDomain: data.data.credentials.store_url,
          consumerKey: data.data.credentials.pos_api_key || "",
          consumerSecret: "" // Do not reveal secret
        });

        const isWp = data.data.pos_system.toLowerCase().includes("wordpress");

        if (isWp) {
          setWpMappings(data.data.mappings.saved_mappings);
          // WP Background discovery will happen when user opens Step 3
        } else {
          setConfirmedMappings(data.data.mappings.saved_mappings);
        }

        if (data.data.filter_config) {
          setPotencyFilterEnabled(data.data.filter_config.potency_enabled);
          setLocationFilterEnabled(data.data.filter_config.location_enabled || false);
          setBudgetRanges(data.data.filter_config.budgets || budgetRanges);
          setCategoryConfig(data.data.filter_config.categories || {});
          setMoodConfig(data.data.filter_config.moods || {});
          setAllCategories(Object.keys(data.data.filter_config.categories || {}));
          setAllMoods(Object.keys(data.data.filter_config.moods || {}));
        }

        // Load stores and POS systems anyway so dropdowns work
        await loadStores();
        await loadPosSystems();

        // Jump to Step 2 if Edit Mode, wait, Step 3 is Mappings.
        // Let's just unlock all steps in Edit Mode via UI, default to activeStep = 2
        setTargetAttributes(data.data.target_attributes || []);
        if (isWp) {
          // Provide a dummy wpDiscovery so dropdowns can read wpDiscovery.target_attributes
          setWpDiscovery({ target_attributes: data.data.target_attributes || [] });
        }

        setActiveStep(1);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load edit configuration.");
    } finally {
      setIsHydrating(false);
    }
  };

  // --- API Calls (Load Data) ---
  const loadStores = async () => {
    setStoresLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/client/pos/available-stores?client_id=${config.clientId}`, {
        headers: config.getHeaders()
      });
      if (!response.ok) throw new Error("Failed to load stores");
      const result = await response.json();
      if (result.success && result.data) {
        setStores(result.data);
      }
    } catch (err) {
      console.error(err);
      setError("Could not load available stores.");
    } finally {
      setStoresLoading(false);
    }
  };

  const loadPosSystems = async () => {
    setPosSystemsLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/client/pos/systems`, {
        headers: config.getHeaders()
      });
      if (!response.ok) throw new Error("Failed to load POS systems");
      const result = await response.json();
      if (result.success && result.data) {
        setPosSystems(result.data);
      }
    } catch (err) {
      setPosSystems([
        { pos_id: "default", pos_name: "Shopify" },
        { pos_id: "dutchie", pos_name: "Dutchie" }
      ]);
    } finally {
      setPosSystemsLoading(false);
    }
  };

  // --- Handlers ---
  const handleInputChange = (field, value) => {
    let updates = { [field]: value };

    // We need to re-evaluate the domain if either store or POS changes
    const currentStoreId = field === "selectedStoreId" ? value : formData.selectedStoreId;
    const currentPos = field === "posSystem" ? value : formData.posSystem;
    const isWordpress = currentPos?.trim().toLowerCase() === "wordpress" || currentPos?.trim().toLowerCase() === "woocommerce";

    if (currentStoreId && (currentPos === "Shopify" || isWordpress)) {
      const selected = stores.find(s => s.client_store_id === currentStoreId);
      let domain = selected?.store_domain || "";

      // Clean domain: remove protocol, www, and any paths/trailing slashes
      if (domain) {
        try {
          // Add dummy protocol if missing so URL parser works
          const urlString = domain.startsWith('http') ? domain : `https://${domain}`;
          const url = new URL(urlString);
          domain = url.hostname.replace(/^www\./, '');
        } catch (e) {
          // Fallback regex if URL parsing fails
          domain = domain.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "").split('/')[0];
        }
      }
      // For Wordpress, we usually need the protocol, but if we prefill it cleaned, that's what the user asked for (just like Shopify).
      updates.storeDomain = domain;
    } else if (currentPos !== "Shopify" && !isWordpress) {
      updates.storeDomain = ""; // Clear for non-Shopify/non-Wordpress
    }

    setFormData(prev => ({ ...prev, ...updates }));
    setError(null);
  };

  // --- Drag & Drop Handlers ---
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };
  const processFile = (file) => {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls') && !fileName.endsWith('.csv')) {
      setError("Invalid file type. Please upload .xlsx, .xls, or .csv");
      setManualFile(null);
      return;
    }
    setManualFile(file);
    setError(null);
  };

  // --- Analyze File (Step 1 -> 2) ---
  const handleAnalyzeFile = async () => {
    const isWp = formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce";
    if ((!manualFile && !isWp) || !formData.selectedStoreId || !formData.posSystem) return;
    // If Wordpress, require successful test before proceeding
    if (isWp && wordpressTestStatus !== "success") {
      setSnackbar({ open: true, message: "Please test WooCommerce connection successfully first.", severity: "error" });
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const isWp = formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce";
      if (isWp) {
        const res = await fetch(`${config.API_BASE_URL}/client/pos/wordpress/discover-fields`, {
          method: "POST",
          headers: config.getHeaders(),
          body: JSON.stringify({
            client_store_id: formData.selectedStoreId,
            store_url: formData.storeDomain,
            consumer_key: formData.consumerKey,
            consumer_secret: formData.consumerSecret
          })
        });
        const result = await res.json();
        if (!res.ok || !result.success) throw new Error(result.message || "WordPress field discovery failed");

        const discovery = result.data;
        setWpDiscovery(discovery);

        // Build initial flat mappings list from product and variation sections
        const allKeys = [
          ...(discovery.product_keys || []),
          ...(discovery.variation_keys || [])
        ];
        setWpMappings(allKeys.map(k => ({
          wc_key: k.wc_key,
          source: k.source,
          is_meta: k.is_meta || false,
          sample_value: k.sample_value,
          attribute_name: k.attribute_name || null,
          locked: k.locked || false,
          product_attribute_master_id: (discovery.target_attributes || []).find(a => a.attribute_name === k.attribute_name)?.product_attribute_master_id || null
        })));

        setActiveStep(2);
        return;
      }

      // Existing Universal / Shopify Flow
      const formDataPayload = new FormData();
      if (manualFile) {
        formDataPayload.append("file", manualFile);
      }

      const analyzeUrl = `${config.API_BASE_URL}/client/pos/analyze-excel?client_store_id=${formData.selectedStoreId}&pos_system=${formData.posSystem}`;
      const headers = config.getHeaders();
      delete headers['Content-Type'];

      const resp = await fetch(analyzeUrl, { method: "POST", headers, body: formDataPayload });
      const result = await resp.json();

      if (!resp.ok || !result.success) throw new Error(result.message || "Analysis failed");

      const { suggestions, target_attributes, required_missing, required_missing_hints, samples, col_stats, shopify_auto_fields } = result.data;
      setMappingSuggestions(suggestions);

      // Merge DB results (if in edit mode and previously mapped)
      const mergedMappings = suggestions.map(s => {
        if (isEditMode && confirmedMappings && confirmedMappings.length > 0) {
          const existing = confirmedMappings.find(m => m.excel_col === s.excel_col);
          if (existing && existing.attribute_name) {
            return {
              ...s,
              attribute_name: existing.attribute_name,
              product_attribute_master_id: existing.product_attribute_master_id,
              confidence: 1.0
            };
          }
        }
        return { ...s };
      });

      setConfirmedMappings(mergedMappings);
      setTargetAttributes(target_attributes || []);
      setRequiredMissing(required_missing || []);
      setRequiredMissingHints(required_missing_hints || []);
      setSampleData(samples || []);
      const statsObj = col_stats || {};
      setColStats(statsObj);
      setShopifyAutoFields(shopify_auto_fields || []);

      const initialPosMapping = suggestions.find(s => s.attribute_name === "pos_product_id");
      if (initialPosMapping) {
        const stats = statsObj[initialPosMapping.excel_col];
        if (stats && stats.uniqueness_pct < 100) {
          setPosIdDupeWarning(`⚠️ Column "${initialPosMapping.excel_col}" is not fully unique (${stats.uniqueness_pct}%).`);
        } else {
          const values = (samples || []).map(row => String(row[initialPosMapping.excel_col] ?? "").trim()).filter(Boolean);
          const hasDupes = values.length > 0 && new Set(values).size < values.length;
          setPosIdDupeWarning(hasDupes ? `⚠️ Column "${initialPosMapping.excel_col}" has duplicate values in samples.` : null);
        }
      } else {
        setPosIdDupeWarning(null);
      }
      setActiveStep(2); // Move to mapping step (index 2 in 3-step flow)
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  // --- WP Auto-Discover for Edit Mode (merges fresh discovery with DB mappings) ---
  const handleWpAutoDiscover = async () => {
    setAnalyzing(true);
    setWpMappingError(null);
    try {
      // In edit mode: use secure endpoint that reads credentials from DB
      // In onboarding: pass credentials from form state
      const url = isEditMode
        ? `${config.API_BASE_URL}/client/pos/wordpress/edit/discover-fields`
        : `${config.API_BASE_URL}/client/pos/wordpress/discover-fields`;
      const body = isEditMode
        ? JSON.stringify({ client_store_id: formData.selectedStoreId })
        : JSON.stringify({
          client_store_id: formData.selectedStoreId,
          store_url: formData.storeDomain,
          consumer_key: formData.consumerKey,
          consumer_secret: formData.consumerSecret
        });
      const res = await fetch(url, { method: "POST", headers: config.getHeaders(), body });
      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Discovery failed");

      const discovery = result.data;
      setWpDiscovery(discovery);
      setTargetAttributes(discovery.target_attributes || []);

      // Build a lookup of existing DB mappings (keyed by wc_key + source)
      const dbMappingLookup = {};
      wpMappings.forEach(m => {
        dbMappingLookup[`${m.wc_key}__${m.source}`] = m;
      });

      // Merge: fresh discovery keys, but use DB attribute_name where available
      const allDiscoveredKeys = [
        ...(discovery.product_keys || []),
        ...(discovery.variation_keys || [])
      ];

      const merged = allDiscoveredKeys.map(k => {
        const lookupKey = `${k.wc_key}__${k.source}`;
        const dbEntry = dbMappingLookup[lookupKey];
        const resolvedAttr = dbEntry?.attribute_name || k.attribute_name || null;
        const resolvedPamId = dbEntry?.product_attribute_master_id ||
          (discovery.target_attributes || []).find(a => a.attribute_name === resolvedAttr)?.product_attribute_master_id || null;
        return {
          wc_key: k.wc_key,
          source: k.source,
          is_meta: k.is_meta || false,
          sample_value: k.sample_value,
          attribute_name: resolvedAttr,
          locked: k.locked || false,
          product_attribute_master_id: resolvedPamId
        };
      });

      // Also keep any DB-mapped keys that weren't found in fresh discovery
      wpMappings.forEach(dbM => {
        const found = allDiscoveredKeys.find(k => k.wc_key === dbM.wc_key && k.source === dbM.source);
        if (!found && dbM.attribute_name) {
          merged.push({ ...dbM, sample_value: dbM.sample_value || '—' });
        }
      });

      setWpMappings(merged);
    } catch (e) {
      setWpMappingError(`Field discovery failed: ${e.message}`);
    } finally {
      setAnalyzing(false);
    }
  };

  // Trigger auto-discover when entering step 2 in WP edit mode
  useEffect(() => {
    const isWp = formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce";
    if (isEditMode && activeStep === 2 && isWp && formData.selectedStoreId) {
      handleWpAutoDiscover();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // --- Save WP Mappings independently ---
  const handleSaveWpMappings = async () => {
    setLoading(true);
    try {
      const mappingsForSave = wpMappings
        .filter(m => m.attribute_name && !m.locked)
        .map(m => ({
          excel_col: m.wc_key,
          attribute_name: m.attribute_name,
          product_attribute_master_id: m.product_attribute_master_id,
          source: m.source,
          confidence: 1.0
        }));
      const mapFormData = new URLSearchParams();
      mapFormData.append("pos_system", "WordPress");
      mapFormData.append("client_store_id", formData.selectedStoreId);
      mapFormData.append("confirmed_mappings", JSON.stringify(mappingsForSave));
      const res = await fetch(`${config.API_BASE_URL}/client/pos/update-excel-mapping`, {
        method: "POST",
        headers: { ...config.getHeaders(), "Content-Type": "application/x-www-form-urlencoded" },
        body: mapFormData.toString()
      });
      if (res.ok) setSnackbar({ open: true, message: "Mappings saved successfully!", severity: "success" });
      else setSnackbar({ open: true, message: "Failed to save mappings.", severity: "error" });
    } catch (e) {
      setSnackbar({ open: true, message: "Failed to save mappings.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // --- Save Filters independently (Edit Mode) ---
  const handleSaveFiltersOnly = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/client/pos/edit/filters`, {
        method: 'POST',
        headers: config.getHeaders(),
        body: JSON.stringify({
          client_store_id: formData.selectedStoreId,
          filter_config: {
            potency_enabled: potencyFilterEnabled,
            location_enabled: locationFilterEnabled,
            mood_enabled: true,
            budget_enabled: true,
            categories: categoryConfig,
            moods: moodConfig,
            budgets: budgetRanges
          }
        })
      });
      if (res.ok) setSnackbar({ open: true, message: "Filters saved successfully!", severity: "success" });
      else setSnackbar({ open: true, message: "Failed to save filters.", severity: "error" });
    } catch (e) {
      setSnackbar({ open: true, message: "Failed to save filters.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // --- Re-Analyze Filters from live store (Edit Mode Step 3) ---
  const handleReanalyzeFilters = async () => {
    setLoading(true);
    try {
      let newCategories = [];
      let newMoods = [];

      // Fetch categories — edit mode uses secure endpoint (DB creds)
      const catRes = await fetch(`${config.API_BASE_URL}/client/pos/wordpress/edit/categories`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({ client_store_id: formData.selectedStoreId })
      });
      const catData = await catRes.json();
      if (catData.success && catData.data) newCategories = catData.data.map(c => c.name);

      // Fetch moods — edit mode uses secure endpoint (DB creds)
      const moodMapping = wpMappings.find(m => m.attribute_name === "mood");
      if (moodMapping) {
        const moodRes = await fetch(`${config.API_BASE_URL}/client/pos/wordpress/edit/unique-moods`, {
          method: "POST",
          headers: config.getHeaders(),
          body: JSON.stringify({
            client_store_id: formData.selectedStoreId,
            wc_key: moodMapping.wc_key,
            source: moodMapping.source
          })
        });
        const moodData = await moodRes.json();
        if (moodData.success && moodData.data?.length > 0) newMoods = moodData.data;
      }

      // Merge: keep existing settings for already-known categories/moods
      const newCatConfig = {};
      newCategories.forEach((cat, idx) => {
        newCatConfig[cat] = categoryConfig[cat] || {
          is_visible: idx < 9, icon_svg: defaultCategorySvg, ask_mood: true,
          ask_potency: false, potency_unit: 'pct',
          low: { min: '', max: '' }, medium: { min: '', max: '' }, high: { min: '', max: '' }
        };
      });
      const newMoodConfig = {};
      newMoods.forEach((mood, idx) => {
        newMoodConfig[mood] = moodConfig[mood] || { is_visible: idx < 12, icon_svg: defaultMoodSvg };
      });

      setCategoryConfig(newCatConfig);
      setMoodConfig(newMoodConfig);
      setAllCategories(newCategories);
      setAllMoods(newMoods);
      setSnackbar({ open: true, message: `Re-analyzed: ${newCategories.length} categories, ${newMoods.length} moods found.`, severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, message: "Re-analysis failed.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // --- Smart Next for non-WP Edit Mode (Step 1 → Step 2) ---
  // If a new file was uploaded: analyze it and merge with DB mappings.
  // If no file: skip straight to Step 2 with existing DB mappings.
  const handleSmartNextForEdit = async () => {
    if (!manualFile) {
      // No new file — navigate directly with existing DB mappings already in state
      setActiveStep(2);
      return;
    }

    // New file uploaded — analyze it
    setAnalyzing(true);
    setError(null);
    try {
      const formDataPayload = new FormData();
      formDataPayload.append("file", manualFile);
      const analyzeUrl = `${config.API_BASE_URL}/client/pos/analyze-excel?client_store_id=${formData.selectedStoreId}&pos_system=${formData.posSystem}`;
      const headers = config.getHeaders();
      delete headers['Content-Type'];
      const resp = await fetch(analyzeUrl, { method: "POST", headers, body: formDataPayload });
      const result = await resp.json();
      if (!resp.ok || !result.success) throw new Error(result.message || "Analysis failed");

      const { suggestions, target_attributes, required_missing, required_missing_hints, samples, col_stats, shopify_auto_fields } = result.data;
      setTargetAttributes(target_attributes || []);
      setRequiredMissing(required_missing || []);
      setRequiredMissingHints(required_missing_hints || []);
      setSampleData(samples || []);
      setColStats(col_stats || {});
      setShopifyAutoFields(shopify_auto_fields || []);

      // Merge fresh suggestions with existing DB mappings
      // DB attribute_name wins for matching columns
      const dbLookup = {};
      confirmedMappings.forEach(m => { if (m.excel_col) dbLookup[m.excel_col] = m; });
      const merged = suggestions.map(s => {
        const db = dbLookup[s.excel_col];
        return {
          ...s,
          attribute_name: db?.attribute_name ?? s.attribute_name ?? null,
          product_attribute_master_id: db?.product_attribute_master_id ?? s.product_attribute_master_id ?? null
        };
      });
      setConfirmedMappings(merged);
      setMappingSuggestions(merged);
      setActiveStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  // --- Re-Analyze Filters for non-WP edit mode (reads from colStats of new file) ---
  const handleReanalyzeFiltersNonWp = () => {
    const productTypeMapping = confirmedMappings.find(m => m.attribute_name === "product_type");
    let newCategories = [];
    if (productTypeMapping && colStats[productTypeMapping.excel_col]?.distinct_samples) {
      newCategories = colStats[productTypeMapping.excel_col].distinct_samples;
    }
    const moodMapping = confirmedMappings.find(m => m.attribute_name === "mood");
    let newMoods = [];
    if (moodMapping && colStats[moodMapping.excel_col]?.distinct_samples) {
      const moodSet = new Set();
      colStats[moodMapping.excel_col].distinct_samples.forEach(m => {
        m.split(',').map(s => s.trim().replace(/^"|"$/g, '')).filter(Boolean).forEach(s => moodSet.add(s));
      });
      newMoods = Array.from(moodSet);
    }

    // Merge: existing DB config wins, new entries are added with defaults
    const newCatConfig = {};
    newCategories.forEach((cat, idx) => {
      newCatConfig[cat] = categoryConfig[cat] || {
        is_visible: idx < 9, icon_svg: defaultCategorySvg, ask_mood: true,
        ask_potency: false, potency_unit: 'pct',
        low: { min: '', max: '' }, medium: { min: '', max: '' }, high: { min: '', max: '' }
      };
    });
    const newMoodConfig = {};
    newMoods.forEach((mood, idx) => {
      newMoodConfig[mood] = moodConfig[mood] || { is_visible: idx < 12, icon_svg: defaultMoodSvg };
    });
    const newCats = newCategories.filter(c => !allCategories.includes(c));
    const newMoodsAdded = newMoods.filter(m => !allMoods.includes(m));
    setCategoryConfig(newCatConfig);
    setMoodConfig(newMoodConfig);
    setAllCategories(newCategories);
    setAllMoods(newMoods);
    setSnackbar({
      open: true,
      message: `Re-analyzed from new file: ${newCats.length > 0 ? `${newCats.length} new categories` : 'no new categories'}, ${newMoodsAdded.length > 0 ? `${newMoodsAdded.length} new moods` : 'no new moods'} found.`,
      severity: "success"
    });
  };

  // --- Mapping Change (Step 2) ---
  const handleExcelMappingChange = (excelCol, newAttributeName) => {
    if (newAttributeName) {
      const alreadyUsed = confirmedMappings.some(m => m.excel_col !== excelCol && m.attribute_name === newAttributeName);
      if (alreadyUsed) {
        setError(`"${newAttributeName}" is already mapped. Bud-i attributes can only be used once.`);
        return;
      }
    }
    setError(null);

    if (newAttributeName === "pos_product_id") {
      const stats = colStats[excelCol];
      if (stats) {
        setPosIdDupeWarning(stats.uniqueness_pct < 100 ? `⚠️ Column "${excelCol}" is not fully unique.` : null);
      } else {
        const values = sampleData.map(row => String(row[excelCol] ?? "").trim()).filter(Boolean);
        setPosIdDupeWarning((values.length > 0 && new Set(values).size < values.length) ? `⚠️ Column "${excelCol}" has duplicate values in samples.` : null);
      }
    } else {
      const wasPosId = confirmedMappings.find(m => m.excel_col === excelCol)?.attribute_name === "pos_product_id";
      if (wasPosId) setPosIdDupeWarning(null);
    }

    const attr = targetAttributes.find(a => a.attribute_name === newAttributeName);
    setConfirmedMappings(prev => prev.map(m =>
      m.excel_col === excelCol
        ? {
          ...m,
          attribute_name: newAttributeName || null,
          product_attribute_master_id: attr?.product_attribute_master_id || null
        }
        : m
    ));
  };

  // --- Filter Configuration Flow (Step 4) ---
  const defaultCategorySvg = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>`;
  const defaultMoodSvg = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" /></svg>`;

  const handleNextToFilters = async (skipLocationCheck = false) => {
    if (isEditMode) {
      // In edit mode, we already loaded the saved config from the DB via fetchEditConfig.
      // We skip auto-discovery here so we don't overwrite user's saved icons/ranges.
      // If the user wants to discover new fields, they can use the "Re-Analyze" button.
      setActiveStep(3);
      return;
    }

    setIsGeneratingIcons(true);

    let categories = [];
    let moods = [];
    let distinctLocations = [];

    const isWp = formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce";

    if (isWp) {
      // ── WordPress Specific ──
      // Edit mode: use secure endpoints (DB credentials). Onboarding: pass from state.
      try {
        const catUrl = isEditMode
          ? `${config.API_BASE_URL}/client/pos/wordpress/edit/categories`
          : `${config.API_BASE_URL}/client/pos/wordpress/categories`;
        const catBody = isEditMode
          ? JSON.stringify({ client_store_id: formData.selectedStoreId })
          : JSON.stringify({
            client_store_id: formData.selectedStoreId,
            store_url: formData.storeDomain,
            consumer_key: formData.consumerKey,
            consumer_secret: formData.consumerSecret
          });
        const res = await fetch(catUrl, { method: "POST", headers: config.getHeaders(), body: catBody });
        const data = await res.json();
        if (data.success && data.data) {
          categories = data.data.map(c => c.name);
        }
      } catch (err) {
        console.error("WP Categories fetch failed:", err);
      }
      // Unique Attributes (Moods & Locations): fetch in one pass if mapped
      const attributes_to_fetch = [];
      const moodMapping = wpMappings.find(m => m.attribute_name === "mood");
      if (moodMapping) {
        attributes_to_fetch.push({ attribute: "mood", wc_key: moodMapping.wc_key, source: moodMapping.source });
      }
      const locMapping = wpMappings.find(m => m.attribute_name === "location");
      if (locMapping) {
        attributes_to_fetch.push({ attribute: "location", wc_key: locMapping.wc_key, source: locMapping.source });
      }

      if (attributes_to_fetch.length > 0) {
        try {
          const attrUrl = isEditMode
            ? `${config.API_BASE_URL}/client/pos/wordpress/edit/unique-attributes`
            : `${config.API_BASE_URL}/client/pos/wordpress/unique-attributes`;
          const attrBody = isEditMode
            ? JSON.stringify({ client_store_id: formData.selectedStoreId, attributes_to_fetch })
            : JSON.stringify({
              client_store_id: formData.selectedStoreId,
              store_url: formData.storeDomain,
              consumer_key: formData.consumerKey,
              consumer_secret: formData.consumerSecret,
              attributes_to_fetch
            });
          const res = await fetch(attrUrl, { method: "POST", headers: config.getHeaders(), body: attrBody });
          const data = await res.json();
          if (data.success && data.data) {
            if (data.data.mood && data.data.mood.length > 0) {
              moods = data.data.mood;
            }
            if (data.data.location) {
              distinctLocations = data.data.location;
            }
          }
        } catch (err) {
          console.error("WP Unique Attributes fetch failed:", err);
        }
      }
      // Potency check is centralized below
    } else {
      // ── Universal / Shopify ──

      // Known cannabis mood/effect words — used to detect space-joined mood lists
      // e.g. Shopify list metafields may export "Dreamy Sleepy Tired Blissful" in one cell
      const KNOWN_MOOD_WORDS = new Set([
        'happy', 'calm', 'relaxed', 'sleepy', 'tired', 'energetic', 'energized',
        'focused', 'creative', 'uplifted', 'euphoric', 'blissful', 'inspired',
        'balanced', 'clear', 'soothing', 'dreamy', 'giggly', 'aroused', 'tingly',
        'talkative', 'motivated', 'peaceful', 'social', 'mindful', 'grounded',
        'dizzy', 'anxious', 'paranoid', 'dry', 'hungry', 'mind', 'smooth',
        'stressless', 'feel-good', 'mood-enhancing', 'light'
      ]);

      // Splits mood cell values by any separator Shopify may use:
      // comma → pipe → semicolon → space (when tokens are known mood words or all single words)
      // e.g. "Happy Sad Relaxed Sleepy"          → ["Happy","Sad","Relaxed","Sleepy"]
      //      "Calm, Relaxed"                      → ["Calm","Relaxed"]
      //      "Calm"                               → ["Calm"]  (single value, untouched)
      //      "Dreamy Sleepy Tired Blissful Relaxed" → ["Dreamy","Sleepy","Tired","Blissful","Relaxed"]
      const splitMoodValues = (raw) => {
        const s = (raw || "").toString().trim().replace(/^"|"$/g, '').replace(/\u00A0/g, ' ');
        if (!s) return [];
        if (s.includes(',')) return s.split(',').map(t => t.trim()).filter(Boolean);
        if (s.includes('|')) return s.split('|').map(t => t.trim()).filter(Boolean);
        if (s.includes(';')) return s.split(';').map(t => t.trim()).filter(Boolean);
        // Split on any whitespace (handles regular space + non-breaking space + tabs)
        const tokens = s.split(/\s+/).map(t => t.trim()).filter(Boolean);
        if (tokens.length <= 1) return [s];
        // Split if ALL tokens are single words (no internal spaces remain)
        const allSingleWords = tokens.every(t => !/\s/.test(t));
        if (!allSingleWords) return [s];
        // Split if at least one token is a known mood word (high confidence it's a list)
        const hasKnownMood = tokens.some(t => KNOWN_MOOD_WORDS.has(t.toLowerCase()));
        if (hasKnownMood) return tokens;
        // Fallback: split anyway if all tokens look like proper nouns / title-case words
        const allTitleCase = tokens.every(t => /^[A-Z][a-z]/.test(t));
        if (allTitleCase) return tokens;
        return [s];
      };

      const productTypeMapping = confirmedMappings.find(m => m.attribute_name === "product_type");
      if (productTypeMapping && colStats[productTypeMapping.excel_col]?.distinct_samples) {
        categories = colStats[productTypeMapping.excel_col].distinct_samples;
      }

      const moodMapping = confirmedMappings.find(m => m.attribute_name === "mood");
      if (moodMapping && colStats[moodMapping.excel_col]?.distinct_samples) {
        const moodSet = new Set();
        colStats[moodMapping.excel_col].distinct_samples.forEach(m => {
          splitMoodValues(m).forEach(v => moodSet.add(v));
        });
        moods = Array.from(moodSet);
      }
      const locMapping = confirmedMappings.find(m => m.attribute_name === "location");
      if (locMapping && colStats[locMapping.excel_col]?.distinct_samples) {
        const locSet = new Set();
        colStats[locMapping.excel_col].distinct_samples.forEach(m => {
          m.split(',').map(s => s.trim().replace(/^"|"$/g, '')).filter(Boolean).forEach(s => locSet.add(s));
        });
        distinctLocations = Array.from(locSet);
      }
    }

    // ── Location Limit Check ──
    const hasLocationMapped = isWp
      ? wpMappings.some(m => m.attribute_name === "location")
      : confirmedMappings.some(m => m.attribute_name === "location");

    if (!skipLocationCheck && hasLocationMapped) {
      const selectedStore = stores.find(s => s.client_store_id === formData.selectedStoreId);
      const planLocationLimit = selectedStore?.number_of_locations || 1;

      if (distinctLocations.length > planLocationLimit) {
        setLocationLimitDialog({
          open: true,
          found: distinctLocations.length,
          allowed: planLocationLimit,
          locations: distinctLocations
        });
        setIsGeneratingIcons(false);
        return; // BLOCK transition
      }
    }
    // ── End Location Limit Check ──

      // No fallback: if no moods found, array remains empty.

      setAllCategories(categories);
      setAllMoods(moods);

      // Smart potency switch: auto-enable ONLY if at least one THC field is mapped
      // Works for all POS systems (WP uses wpMappings, others use confirmedMappings)
      const hasPotencyMapping = isWp
        ? wpMappings.some(m => m.attribute_name === "thc_content_pct" || m.attribute_name === "thc_content_mg")
        : confirmedMappings.some(m => m.attribute_name === "thc_content_pct" || m.attribute_name === "thc_content_mg");

      // Always set explicitly: true if mapped, false if not (overrides any previous state)
      setPotencyFilterEnabled(hasPotencyMapping);
      const localPotencyEnabled = hasPotencyMapping;

      const getDefaultPotency = (catName) => {
        if (!localPotencyEnabled) return { ask_potency: false, potency_unit: "pct", low: { min: "", max: "" }, medium: { min: "", max: "" }, high: { min: "", max: "" } };

        const lower = catName.toLowerCase();
        if (lower.includes('flower') || lower.includes('pre-roll') || lower.includes('preroll')) {
          return { ask_potency: true, potency_unit: 'pct', low: { min: 0, max: 15 }, medium: { min: 15, max: 22 }, high: { min: 22, max: 40 } };
        }
        if (lower.includes('vaporizer') || lower.includes('vape') || lower.includes('concentrate') || lower.includes('extract') || lower.includes('cart')) {
          return { ask_potency: true, potency_unit: 'pct', low: { min: 0, max: 60 }, medium: { min: 60, max: 80 }, high: { min: 80, max: 100 } };
        }
        if (lower.includes('edible') || lower.includes('gummy') || lower.includes('gummies') || lower.includes('beverage') || lower.includes('drink')) {
          return { ask_potency: true, potency_unit: 'mg', low: { min: 0, max: 5 }, medium: { min: 5, max: 20 }, high: { min: 20, max: 100 } };
        }
        if (lower.includes('tincture') || lower.includes('topical') || lower.includes('lotion') || lower.includes('balm')) {
          return { ask_potency: true, potency_unit: 'mg', low: { min: 0, max: 100 }, medium: { min: 100, max: 500 }, high: { min: 500, max: 2000 } };
        }
        // Disabled by default for unknown/other categories
        return { ask_potency: false, potency_unit: "pct", low: { min: "", max: "" }, medium: { min: "", max: "" }, high: { min: "", max: "" } };
      };

      // Initial dummy state so the UI renders something immediately
      const catObj = {};
      categories.forEach((cat, idx) => {
        const potencyDefaults = getDefaultPotency(cat);
        catObj[cat] = {
          is_visible: idx < 9,
          icon_svg: defaultCategorySvg,
          ask_mood: true,
          ...potencyDefaults
        };
      });
      setCategoryConfig(catObj);

      const moodObj = {};
      moods.forEach((m, idx) => {
        moodObj[m] = {
          is_visible: idx < 12,
          icon_svg: defaultMoodSvg
        };
      });
      setMoodConfig(moodObj);

      // Fetch Icons from Backend LLM Service asynchronously
      try {
        const res = await fetch(`${config.API_BASE_URL}/client/pos/generate-icons`, {
          method: "POST",
          headers: config.getHeaders(),
          body: JSON.stringify({ categories, moods })
        });
        const data = await res.json();
        if (data.success) {
          const fetchedCategories = data.data.categories || {};
          const fetchedMoods = data.data.moods || {};

          // Update states with fetched SVGs
          setCategoryConfig(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(c => {
              if (fetchedCategories[c]) next[c].icon_svg = fetchedCategories[c];
            });
            return next;
          });

          setMoodConfig(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(m => {
              if (fetchedMoods[m]) next[m].icon_svg = fetchedMoods[m];
            });
            return next;
          });
        }
      } catch (e) {
        console.error("Failed to fetch custom icons", e);
      } finally {
        setIsGeneratingIcons(false);
        setActiveStep(3); // Land on Step 3: Configure Filters
      }
    };

    const handleCategoryConfigChange = (category, field, subfield, value) => {
      if (field === 'is_visible' && value === true) {
        const activeCount = Object.values(categoryConfig).filter(c => c.is_visible).length;
        if (activeCount >= 9) {
          setSnackbar({ open: true, message: "You can only have up to 9 visible categories.", severity: "warning" });
          return;
        }
      }
      setCategoryConfig(prev => {
        const newConfig = { ...prev };
        if (subfield) {
          newConfig[category][field][subfield] = value;
        } else {
          newConfig[category][field] = value;
        }
        return newConfig;
      });
    };

    const handleMoodConfigChange = (mood, field, value) => {
      if (field === 'is_visible' && value === true) {
        const activeCount = Object.values(moodConfig).filter(m => m.is_visible).length;
        if (activeCount >= 12) {
          setSnackbar({ open: true, message: "You can only have up to 12 visible moods.", severity: "warning" });
          return;
        }
      }
      setMoodConfig(prev => ({
        ...prev,
        [mood]: { ...prev[mood], [field]: value }
      }));
    };

    const handleBudgetChange = (idx, field, value) => {
      setBudgetRanges(prev => {
        const newB = [...prev];
        newB[idx][field] = value;
        return newB;
      });
    };

    // --- Final Upload ---
    const handleEditSave = async () => {
      setLoading(true);
      try {
        // 1. Save Filters (always)
        await fetch(`${config.API_BASE_URL}/client/pos/edit/filters`, {
          method: 'POST',
          headers: config.getHeaders(),
          body: JSON.stringify({
            client_store_id: formData.selectedStoreId,
            filter_config: {
              potency_enabled: potencyFilterEnabled,
              mood_enabled: true,
              budget_enabled: true,
              categories: categoryConfig,
              moods: moodConfig,
              budgets: budgetRanges
            }
          })
        });

        // 2. Save Mappings
        const isWp = formData.posSystem?.trim().toLowerCase().includes("wordpress");
        const mappingsToSave = isWp ? wpMappings : confirmedMappings;

        const mapFormData = new URLSearchParams();
        mapFormData.append("pos_system", formData.posSystem);
        mapFormData.append("client_store_id", formData.selectedStoreId);
        mapFormData.append("confirmed_mappings", JSON.stringify(mappingsToSave));

        await fetch(`${config.API_BASE_URL}/client/pos/update-excel-mapping`, {
          method: "POST",
          headers: {
            ...config.getHeaders(),
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: mapFormData.toString(),
        });

        setSnackbar({ open: true, message: "Configuration saved! You may want to run a Quick Sync if you changed mappings.", severity: "success" });
        setTimeout(() => navigate('/pos-sync'), 3000);
      } catch (e) {
        console.error(e);
        setSnackbar({ open: true, message: "Failed to save configuration.", severity: "error" });
      } finally {
        setLoading(false);
      }
    };

    const handleManualUpload = async () => {
      setLoading(true);
      setError(null);
      setSnackbar({ open: true, message: "Sync process started. This may take a moment...", severity: "info" });

      try {
        const formDataPayload = new FormData();
        if (manualFile) {
          formDataPayload.append("file", manualFile);
        }

        const filterConfigPayload = {
          potency_enabled: potencyFilterEnabled,
          location_enabled: locationFilterEnabled,
          mood_enabled: true,
          budget_enabled: true,
          categories: categoryConfig,
          moods: moodConfig,
          budgets: budgetRanges
        };

        const isWp = formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce";

        if (isWp) {
          // Step 1: Save field mappings to product_field_mapping
          const mappingsForSave = wpMappings
            .filter(m => m.attribute_name && !m.locked)
            .map(m => ({
              excel_col: m.wc_key,
              attribute_name: m.attribute_name,
              product_attribute_master_id: m.product_attribute_master_id,
              source: m.source,
              confidence: 1.0
            }));

          const mappingFormData = new URLSearchParams();
          mappingFormData.append("pos_system", "WordPress");
          mappingFormData.append("client_store_id", formData.selectedStoreId);
          mappingFormData.append("confirmed_mappings", JSON.stringify(mappingsForSave));
          await fetch(`${config.API_BASE_URL}/client/pos/update-excel-mapping`, {
            method: "POST",
            headers: { ...config.getHeaders(), "Content-Type": "application/x-www-form-urlencoded" },
            body: mappingFormData.toString()
          });

          // Step 2: Build wc_config from confirmed mappings for the sync service
          const moodMapping = wpMappings.find(m => m.attribute_name === "mood");
          const thcPctMapping = wpMappings.find(m => m.attribute_name === "thc_content_pct");
          const thcMgMapping = wpMappings.find(m => m.attribute_name === "thc_content_mg");
          const cbdMapping = wpMappings.find(m => m.attribute_name === "cbd_content_pct");
          const wpAllowedMoods = allMoods.filter(m => moodConfig[m]?.is_visible);
          const wcConfig = {
            mood_source: moodMapping ? (moodMapping.source === "meta" ? "metadata" : "tags") : "none",
            mood_key: moodMapping?.wc_key || null,
            allowed_moods: wpAllowedMoods,
            potency_enabled: potencyFilterEnabled,
            potency_source: thcPctMapping?.wc_key || thcMgMapping?.wc_key || null,
            cbd_source: cbdMapping?.wc_key || null
          };

          // Step 3: Run the full sync
          const uploadUrl = `${config.API_BASE_URL}/client/pos/wordpress/sync`;
          const wpPayload = {
            client_store_id: formData.selectedStoreId,
            store_url: formData.storeDomain,
            consumer_key: formData.consumerKey,
            consumer_secret: formData.consumerSecret,
            wc_config: wcConfig,
            filter_config: filterConfigPayload
          };

          const res = await fetch(uploadUrl, {
            method: "POST",
            headers: config.getHeaders(),
            body: JSON.stringify(wpPayload)
          });
          const result = await res.json();
          if (!res.ok || !result.success) throw new Error(result.message || "WordPress Sync failed");

          const { inserted = 0, updated = 0 } = result.data || {};
          setSnackbar({ open: true, message: `✅ WordPress sync complete! Inserted ${inserted}, Updated ${updated}.`, severity: "success" });

        } else if (formData.posSystem === "Shopify") {
          // Shopify flow
          formDataPayload.append("filter_config", JSON.stringify(filterConfigPayload));
          if (!formData.storeDomain) throw new Error("Shopify Store Domain is required.");
          formDataPayload.append("confirmed_mappings", JSON.stringify(confirmedMappings));
          const uploadUrl = `${config.API_BASE_URL}/client/pos/upload-shopify-mapped?store_url=${formData.storeDomain}&client_store_id=${formData.selectedStoreId}`;
          const headers = config.getHeaders();
          delete headers['Content-Type'];

          const res = await fetch(uploadUrl, { method: "POST", headers, body: formDataPayload });
          const result = await res.json();
          if (!res.ok || !result.success) throw new Error(result.message || "Upload failed");

          const { inserted = 0, updated = 0 } = result.data || {};
          setSnackbar({ open: true, message: `✅ Shopify sync complete! Inserted ${inserted}, Updated ${updated}.`, severity: "success" });
        } else {
          // Universal flow for all other POS
          formDataPayload.append("filter_config", JSON.stringify(filterConfigPayload));
          formDataPayload.append("confirmed_mappings", JSON.stringify(confirmedMappings));
          const uploadUrl = `${config.API_BASE_URL}/client/pos/upload-universal?client_store_id=${formData.selectedStoreId}&pos_system=${formData.posSystem}`;
          const headers = config.getHeaders();
          delete headers['Content-Type'];

          const res = await fetch(uploadUrl, { method: "POST", headers, body: formDataPayload });
          const result = await res.json();
          if (!res.ok || !result.success) throw new Error(result.message || "Upload failed");

          setSnackbar({ open: true, message: `✅ Sync complete! Inserted ${result.data?.inserted || 0}, Updated ${result.data?.updated || 0}.`, severity: "success" });
        }
        setTimeout(() => navigate("/pos-sync"), 3000);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };


    // --- UI renderers ---

    const steps = [
      { label: "Select Store & POS", icon: <StorefrontIcon /> },
      { label: "Upload Data", icon: <CloudUploadIcon /> },
      { label: "AI Analysis", icon: <AutoFixHighIcon /> },
      { label: "Customize Filters", icon: <TuneIcon /> }
    ];

    const handleDownloadTemplate = () => {
      const headers = ["product_name", "product_url", "image_url", "product_type", "product_price", "product_description", "thc_content_pct", "thc_content_mg", "mood", "location", "product_identifier", "is_available"];
      const row1 = ["Sample Pre-Roll 1", "https://example.com/product-1", "https://example.com/img1.jpg", "Pre-Rolls", "$48.00", "Classic energetic strain", "30.6%", "", '"Energetic, Happy"', '"Downtown, Uptown"', "sample-preroll-1", "TRUE"];
      const row2 = ["Sample Vaporizer 2", "https://example.com/product-2", "https://example.com/img2.jpg", "Vaporizers", "$65.00", "Ripe fruity flavor", "", "850", '"Relaxed, Sleepy"', "Midtown", "sample-vaporizer-2", "TRUE"];
      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), row1.join(","), row2.join(",")].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "Bud_i_Product_Template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <Box sx={{ maxWidth: 1000, mx: "auto", py: 4 }}>

        {/* Hydration Spinner */}
        {isHydrating && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">Loading Store Configuration...</Typography>
          </Box>
        )}

        {/* Header & Stepper */}
        {!isHydrating && (
          <>
            <Box sx={{ mb: 5, textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "text.primary", mb: 1, letterSpacing: "-0.5px" }}>
                Data Synchronization
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: "auto" }}>
                Seamlessly import your product catalog into Bud-i via Excel or CSV.
              </Typography>

              <Stepper activeStep={activeStep} alternativeLabel sx={{
                '& .MuiStepConnector-line': { borderColor: theme.palette.divider, borderWidth: 2 },
                '& .Mui-active .MuiStepIcon-root': { color: theme.palette.primary.main },
                '& .Mui-completed .MuiStepIcon-root': { color: theme.palette.success.main }
              }}>
                {steps.map((step, idx) => (
                  <Step key={step.label} completed={activeStep > idx}>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box sx={{
                          width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: activeStep > idx ? 'success.main' : activeStep === idx ? 'primary.main' : 'grey.200',
                          color: activeStep >= idx ? 'white' : 'grey.500',
                          boxShadow: activeStep === idx ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}` : 'none',
                          transition: 'all 0.3s ease'
                        }}>
                          {activeStep > idx ? <CheckCircleOutlineIcon /> : step.icon}
                        </Box>
                      )}
                    >
                      <Typography sx={{ fontWeight: activeStep === idx ? 700 : 500, mt: 1 }}>{step.label}</Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* Main Content Area */}
            <Card sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflow: 'visible'
            }}>

              {/* Alerts */}
              <Fade in={!!error}><Box>{error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}</Box></Fade>
              <Fade in={!!success}><Box>{success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}</Box></Fade>

              {/* STEP 0: Selection */}
              {activeStep === 0 && (
                <Fade in={true} timeout={500}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 500, mx: 'auto' }}>
                    <FormControl fullWidth>
                      <InputLabel>Client Store</InputLabel>
                      <Select
                        value={formData.selectedStoreId}
                        onChange={(e) => handleInputChange("selectedStoreId", e.target.value)}
                        label="Client Store"
                        sx={{ borderRadius: 2 }}
                      >
                        {storesLoading ? <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading...</MenuItem> :
                          stores.map(s => <MenuItem key={s.client_store_id} value={s.client_store_id}>{s.store_name}</MenuItem>)
                        }
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>POS System / Source</InputLabel>
                      <Select
                        value={formData.posSystem}
                        onChange={(e) => handleInputChange("posSystem", e.target.value)}
                        label="POS System / Source"
                        sx={{ borderRadius: 2 }}
                      >
                        {posSystemsLoading ? <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading...</MenuItem> :
                          posSystems.map(s => <MenuItem key={s.pos_id} value={s.pos_name}>{s.pos_name}</MenuItem>)
                        }
                      </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={() => setActiveStep(1)}
                        disabled={!formData.selectedStoreId || !formData.posSystem}
                        sx={{ py: 1.5, borderRadius: 2, fontWeight: 700 }}
                      >
                        Continue
                      </Button>
                    </Box>
                  </Box>
                </Fade>
              )}

              {/* STEP 1: Upload Dropzone */}
              {activeStep === 1 && (
                <Fade in={true} timeout={500}>
                  <Box>
                    {/* Conditional Download Template for non-Shopify and non-Wordpress */}
                    {formData.posSystem !== "Shopify" && formData.posSystem?.trim().toLowerCase() !== "wordpress" && formData.posSystem?.trim().toLowerCase() !== "woocommerce" && (
                      <Box sx={{ mb: 4, p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.1), display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                            Standardized Product Template
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Download our template to ensure your data is formatted correctly before uploading.
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={handleDownloadTemplate}
                          startIcon={<DownloadIcon />}
                          sx={{ flexShrink: 0, borderRadius: 2, fontWeight: 600, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                        >
                          Download Template
                        </Button>
                      </Box>
                    )}

                    {/* Top Summary Bar */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Target Store</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {formData.storeName || stores.find(s => s.client_store_id === formData.selectedStoreId)?.store_name || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{formData.posSystem}</Typography>
                      </Box>
                      <Button size="small" variant="text" onClick={() => setActiveStep(0)} sx={{ textTransform: 'none' }}>Change</Button>
                    </Box>

                    {formData.posSystem === "Shopify" && (
                      <TextField
                        fullWidth
                        label="Shopify Store Domain"
                        value={formData.storeDomain}
                        sx={{ mb: 3 }}
                        InputProps={{ sx: { borderRadius: 2 }, readOnly: true }}
                        helperText="Make sure this is your store's domain. If not, please edit it in Store Management and continue."
                      />
                    )}

                    {(formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce") && (
                      <Box sx={{ mb: 4, p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>API Credentials</Typography>
                          {isEditMode && (
                            <Button size="small" variant="text" onClick={() => setIsEditingWpCreds(!isEditingWpCreds)} sx={{ fontWeight: 600 }}>
                              {isEditingWpCreds ? "Cancel Edit" : "Edit Credentials"}
                            </Button>
                          )}
                        </Box>

                        {isEditMode && !isEditingWpCreds ? (
                          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2, border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.2), display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CheckCircleOutlineIcon color="success" />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.dark' }}>Connected to {formData.storeDomain}</Typography>
                              <Typography variant="caption" color="text.secondary">Using Key: {formData.consumerKey || 'Protected'}</Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Box>
                            {isEditMode && (
                              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                                Your API credentials are securely stored. You only need to enter a new Consumer Key and Secret if you recently regenerated them in WooCommerce.
                              </Alert>
                            )}
                            <TextField
                              fullWidth
                              label="Store URL"
                              value={formData.storeDomain}
                              sx={{ mb: 2 }}
                              InputProps={{ sx: { borderRadius: 2 }, readOnly: true }}
                              helperText="Make sure this is your store's domain. If not, please edit it in Store Management and continue."
                            />
                            <TextField
                              fullWidth
                              label="Consumer Key"
                              value={formData.consumerKey}
                              onChange={(e) => handleInputChange("consumerKey", e.target.value)}
                              sx={{ mb: 2 }}
                              placeholder="ck_XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                            />
                            <TextField
                              fullWidth
                              label={isEditMode ? "New Consumer Secret" : "Consumer Secret"}
                              type="password"
                              value={formData.consumerSecret}
                              onChange={(e) => handleInputChange("consumerSecret", e.target.value)}
                              sx={{ mb: 3 }}
                              placeholder={isEditMode ? "**********************************" : "cs_XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"}
                            />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Button
                                variant={wordpressTestStatus === "success" ? "contained" : "outlined"}
                                color={wordpressTestStatus === "success" ? "success" : wordpressTestStatus === "error" ? "error" : "primary"}
                                onClick={async () => {
                                  if (!formData.storeDomain || !formData.consumerKey || !formData.consumerSecret) {
                                    setWordpressTestError("All fields are required.");
                                    return;
                                  }
                                  setWordpressTestStatus("testing");
                                  setWordpressTestError("");
                                  try {
                                    const res = await fetch(`${config.API_BASE_URL}/client/pos/wordpress/test`, {
                                      method: 'POST',
                                      headers: config.getHeaders(),
                                      body: JSON.stringify({
                                        client_store_id: formData.selectedStoreId,
                                        store_url: formData.storeDomain,
                                        consumer_key: formData.consumerKey,
                                        consumer_secret: formData.consumerSecret
                                      })
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      setWordpressTestStatus("success");
                                      setSnackbar({ open: true, message: data.message, severity: "success" });
                                    } else {
                                      setWordpressTestStatus("error");
                                      setWordpressTestError(data.message);
                                    }
                                  } catch (err) {
                                    setWordpressTestStatus("error");
                                    setWordpressTestError("Network error. Please try again.");
                                  }
                                }}
                                disabled={wordpressTestStatus === "testing"}
                                startIcon={
                                  wordpressTestStatus === "testing" ? <CircularProgress size={20} /> :
                                    wordpressTestStatus === "success" ? <CheckCircleOutlineIcon /> :
                                      <span style={{ display: 'none' }} />
                                }
                              >
                                {wordpressTestStatus === "testing" ? "Testing Connection..." : wordpressTestStatus === "success" ? "Connected" : "Test Connection"}
                              </Button>

                              {isEditMode && (
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  disabled={wordpressTestStatus !== "success"}
                                  onClick={async () => {
                                    if (!formData.storeDomain || !formData.consumerKey || !formData.consumerSecret) {
                                      setWordpressTestError("All fields are required to update.");
                                      return;
                                    }
                                    setWordpressTestError("");
                                    try {
                                      const res = await fetch(`${config.API_BASE_URL}/client/pos/wordpress/update-credentials`, {
                                        method: 'POST',
                                        headers: config.getHeaders(),
                                        body: JSON.stringify({
                                          client_store_id: formData.selectedStoreId,
                                          store_url: formData.storeDomain,
                                          consumer_key: formData.consumerKey,
                                          consumer_secret: formData.consumerSecret
                                        })
                                      });
                                      const data = await res.json();
                                      if (data.success) {
                                        setSnackbar({ open: true, message: "Credentials updated successfully!", severity: "success" });
                                        setWordpressTestStatus("success");
                                        setIsEditingWpCreds(false); // Collapse on success
                                      } else {
                                        setWordpressTestError(data.message);
                                      }
                                    } catch (err) {
                                      setWordpressTestError("Failed to update credentials.");
                                    }
                                  }}
                                >
                                  Update Credentials
                                </Button>
                              )}
                            </Box>
                            {wordpressTestError && (
                              <Typography color="error" variant="body2" sx={{ mt: 1 }}>{wordpressTestError}</Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Dropzone */}
                    {(formData.posSystem?.trim().toLowerCase() !== "wordpress" && formData.posSystem?.trim().toLowerCase() !== "woocommerce") && (
                      <Box
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{
                          border: '2px dashed',
                          borderColor: isDragging ? 'primary.main' : manualFile ? 'success.main' : 'grey.300',
                          bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.05) : manualFile ? alpha(theme.palette.success.main, 0.02) : 'grey.50',
                          borderRadius: 4,
                          p: 6,
                          textAlign: 'center',
                          transition: 'all 0.2s ease-in-out',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: manualFile ? 'success.dark' : 'primary.main',
                            bgcolor: manualFile ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                        onClick={() => document.getElementById("file-upload").click()}
                      >
                        <input
                          type="file"
                          id="file-upload"
                          style={{ display: 'none' }}
                          accept=".xlsx, .xls, .csv"
                          onChange={handleFileSelect}
                        />

                        {manualFile ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
                            <Box>
                              <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>{manualFile.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{(manualFile.size / 1024).toFixed(1)} KB</Typography>
                            </Box>
                            <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 500, textDecoration: 'underline' }}>
                              Click or drag to replace
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <CloudUploadIcon color={isDragging ? "primary" : "action"} sx={{ fontSize: 60, opacity: 0.8 }} />
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Drag & Drop your Export
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                or click to browse files (.xlsx, .csv)
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Edit Mode Info Banner for non-WP */}
                    {isEditMode && formData.posSystem && formData.posSystem?.trim().toLowerCase() !== "wordpress" && formData.posSystem?.trim().toLowerCase() !== "woocommerce" && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2, border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.2), display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <InfoOutlinedIcon color="info" sx={{ mt: 0.2, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'info.dark' }}>Existing mappings are loaded from the database.</Typography>
                          <Typography variant="body2" color="text.secondary">Upload a new export file only if you want to update or add column mappings. Skip the upload to continue with your current mappings.</Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                      {/* WP Edit Mode: just a Next button — discovery happens automatically on Step 2 */}
                      {(formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce") && isEditMode ? (
                        <Button
                          variant="contained"
                          size="large"
                          onClick={() => setActiveStep(2)}
                          sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700 }}
                        >
                          Next: Review Mappings →
                        </Button>
                      ) : isEditMode ? (
                        // Non-WP Edit Mode: smart Next (optional file)
                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleSmartNextForEdit}
                          disabled={analyzing || (formData.posSystem === "Shopify" && !formData.storeDomain)}
                          startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                          sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700 }}
                        >
                          {analyzing ? "Analyzing..." : manualFile ? "Analyze & Next →" : "Next: Review Mappings →"}
                        </Button>
                      ) : (
                        // Onboarding: standard Analyze button
                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleAnalyzeFile}
                          disabled={
                            (!manualFile && formData.posSystem?.trim().toLowerCase() !== "wordpress" && formData.posSystem?.trim().toLowerCase() !== "woocommerce") ||
                            analyzing ||
                            (formData.posSystem === "Shopify" && !formData.storeDomain) ||
                            ((formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce") && wordpressTestStatus !== "success")
                          }
                          startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
                          sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700 }}
                        >
                          {analyzing ? "Analyzing..." : "Analyze"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Fade>
              )}

              {/* STEP 2: Mapping Review / Gatekeeper */}
              {activeStep === 2 && (
                <Fade in={true} timeout={500}>
                  <Box>
                    {(formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce") ? (
                      // ── WordPress Field Mapping UI ──
                      <Box sx={{ maxWidth: '960px', mx: 'auto' }}>

                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Review Field Mapping</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {analyzing ? "Discovering fields from your store..." : "Fields pre-mapped from your store. Review and adjust — focus on Mood, THC, and CBD."}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                            {!analyzing && isEditMode && (
                              <Button
                                variant="outlined"
                                size="small"
                                color="info"
                                onClick={handleWpAutoDiscover}
                                startIcon={<AutoFixHighIcon />}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                              >
                                Re-Discover Fields
                              </Button>
                            )}
                            {!analyzing && (
                              <Button variant="outlined" size="small" onClick={() => setActiveStep(1)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                                ← Back
                              </Button>
                            )}
                          </Box>
                        </Box>

                        {/* Centered Discovery Loading State */}
                        {analyzing && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, mb: 3, bgcolor: alpha(theme.palette.info.main, 0.02), borderRadius: 2, border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.1) }}>
                            <CircularProgress size={32} color="info" sx={{ mb: 2 }} />
                            <Typography variant="subtitle2" color="info.main" sx={{ fontWeight: 700 }}>Scanning Store...</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Merging updated fields with your saved configuration.</Typography>
                          </Box>
                        )}

                        {wpMappingError && (
                          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>{wpMappingError}</Alert>
                        )}

                        {!analyzing && ['product', 'variation'].map((section) => {
                          const sectionKeys = wpMappings.filter(m => m.source === section);
                          if (sectionKeys.length === 0) return null;
                          const sectionMeta = {
                            product: { label: 'Product Fields', sub: 'Product-level fields and product meta_data.' },
                            variation: { label: 'Variation Fields', sub: 'Fields from product variations and variation meta_data' }
                          };
                          return (
                            <Accordion
                              key={section}
                              defaultExpanded={true}
                              disableGutters
                              sx={{ mb: 2, borderRadius: '8px !important', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' }, boxShadow: 'none', overflow: 'hidden' }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', px: 3, minHeight: 'unset' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{sectionMeta[section].label}</Typography>
                                  <Chip label={`${sectionKeys.length} keys`} size="small" sx={{ fontWeight: 700, bgcolor: 'grey.200', color: 'text.secondary' }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mr: 1, display: { xs: 'none', sm: 'block' }, alignSelf: 'center' }}>
                                  {sectionMeta[section].sub}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 0 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 2fr 90px', bgcolor: 'grey.100', px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>WC Key</Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Sample Value</Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Bud-I Attribute</Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', textAlign: 'center' }}>Status</Typography>
                                </Box>
                                <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                                  {sectionKeys.map((mapping, idx) => {
                                    const isLocked = mapping.locked;
                                    const isMapped = !!mapping.attribute_name;
                                    return (
                                      <Box
                                        key={`${section}-${mapping.wc_key}`}
                                        sx={{
                                          display: 'grid', gridTemplateColumns: '2fr 1.2fr 2fr 90px', gap: 1.5, alignItems: 'center',
                                          px: 3, py: 1.5,
                                          borderBottom: idx < sectionKeys.length - 1 ? '1px solid' : 'none',
                                          borderColor: 'divider',
                                          bgcolor: isLocked ? alpha(theme.palette.primary.main, 0.02) : 'background.paper',
                                          '&:hover': { bgcolor: isLocked ? alpha(theme.palette.action.hover, 0.4) : alpha(theme.palette.primary.main, 0.02) }
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: isLocked ? alpha(theme.palette.text.primary, 0.6) : 'text.primary' }}>
                                            {mapping.wc_key}
                                          </Typography>
                                          {mapping.is_meta && (
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.65rem', border: '1px solid', borderColor: 'primary.light', borderRadius: 1, px: 0.5 }}>
                                              Meta
                                            </Typography>
                                          )}
                                          {isLocked && (
                                            <Chip label="Auto" size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.dark' }} />
                                          )}
                                        </Box>
                                        <Tooltip title={String(mapping.sample_value || '—')}>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: isLocked ? 0.7 : 1 }}>
                                            {mapping.sample_value || '—'}
                                          </Typography>
                                        </Tooltip>
                                        <FormControl fullWidth size="small">
                                          <Select
                                            key={`select-${mapping.wc_key}-${mapping.source}-${mapping.attribute_name || 'none'}`}
                                            value={mapping.attribute_name || ''}
                                            onChange={(e) => {
                                              if (isLocked) return;
                                              const newAttr = e.target.value || null;
                                              const alreadyUsed = wpMappings.some(m =>
                                                (m.wc_key !== mapping.wc_key || m.source !== mapping.source) && m.attribute_name === newAttr
                                              );
                                              if (alreadyUsed && newAttr) {
                                                setWpMappingError(`"${newAttr}" is already mapped. Each Bud-e attribute can only be used once.`);
                                                return;
                                              }
                                              setWpMappingError(null);
                                              const attr = (wpDiscovery?.target_attributes || []).find(a => a.attribute_name === newAttr);
                                              setWpMappings(prev => prev.map(m =>
                                                m.wc_key === mapping.wc_key && m.source === mapping.source
                                                  ? { ...m, attribute_name: newAttr, product_attribute_master_id: attr?.product_attribute_master_id || null }
                                                  : m
                                              ));
                                            }}
                                            disabled={isLocked}
                                            displayEmpty
                                            renderValue={(selected) => {
                                              if (!selected) return <em style={{ color: 'grey' }}>— Skip / Unmapped —</em>;
                                              return selected;
                                            }}
                                            sx={{
                                              borderRadius: 1.5,
                                              bgcolor: isLocked ? alpha(theme.palette.action.disabledBackground, 0.1) : 'background.paper',
                                              '& .MuiSelect-select': { py: 1, color: isLocked ? 'text.primary' : 'inherit' },
                                              '& .MuiOutlinedInput-notchedOutline': { borderColor: isLocked ? 'divider' : 'inherit' }
                                            }}
                                          >
                                            <MenuItem value=""><Typography variant="body2" color="text.secondary"><em>— Skip / Unmapped —</em></Typography></MenuItem>
                                            {(wpDiscovery?.target_attributes || []).map(attr => {
                                              const isUsedElsewhere = wpMappings.some(m =>
                                                (m.wc_key !== mapping.wc_key || m.source !== mapping.source) && m.attribute_name === attr.attribute_name
                                              );
                                              return (
                                                <MenuItem key={attr.product_attribute_master_id} value={attr.attribute_name} disabled={isUsedElsewhere}>
                                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', opacity: isUsedElsewhere ? 0.5 : 1 }}>
                                                    <Typography variant="body2">{attr.attribute_name}{attr.required && <span style={{ color: theme.palette.error.main }}> *</span>}</Typography>
                                                    {isUsedElsewhere && <CheckCircleOutlineIcon fontSize="small" color="action" />}
                                                  </Box>
                                                </MenuItem>
                                              );
                                            })}
                                          </Select>
                                        </FormControl>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                          {isLocked ? (
                                            <Chip label="Locked" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.dark' }} />
                                          ) : (
                                            <>
                                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isMapped ? '#4caf50' : 'grey.300', boxShadow: isMapped ? '0 0 8px rgba(76,175,80,0.5)' : 'none' }} />
                                              <Typography variant="body2" sx={{ fontWeight: 700, color: isMapped ? 'text.primary' : 'text.disabled' }}>
                                                {isMapped ? 'Mapped' : '—'}
                                              </Typography>
                                            </>
                                          )}
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}

                        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                          {!wpMappings.some(m => m.attribute_name === 'mood') && (
                            <Typography variant="caption" color="error.main" sx={{ fontWeight: 600, display: 'block', mb: 2 }}>
                              * Mood attribute must be mapped to proceed.
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button
                              variant="outlined"
                              size="large"
                              onClick={() => setActiveStep(1)}
                              sx={{ py: 1.5, px: 4, borderRadius: 2, fontWeight: 700 }}
                            >
                              ← Back to Credentials
                            </Button>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              {isEditMode && (
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  size="large"
                                  onClick={handleSaveWpMappings}
                                  disabled={loading || analyzing}
                                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineIcon />}
                                  sx={{ py: 1.5, px: 4, borderRadius: 2, fontWeight: 700 }}
                                >
                                  Save Mappings
                                </Button>
                              )}
                              <Button
                                variant="contained"
                                size="large"
                                onClick={() => handleNextToFilters()}
                                disabled={!wpMappings.some(m => m.attribute_name === 'mood') || analyzing || isGeneratingIcons}
                                startIcon={isGeneratingIcons ? <CircularProgress size={18} color="inherit" /> : null}
                                sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700 }}
                              >
                                {isGeneratingIcons ? "Analyzing Data..." : "Next: Customize Filters →"}
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      // ── Universal / Shopify Mapping UI ──
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Review Data Mapping</Typography>
                            <Typography variant="body2" color="text.secondary">
                              We've automatically matched your {confirmedMappings.length} columns. Please review required fields.
                            </Typography>
                          </Box>
                          <Button variant="outlined" size="small" onClick={() => setActiveStep(1)} sx={{ borderRadius: 2, textTransform: 'none' }}>
                            Back to Upload
                          </Button>
                        </Box>

                        {requiredMissingHints.filter(h => !confirmedMappings.some(m => m.attribute_name === h.attribute)).length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            {requiredMissingHints
                              .filter(h => !confirmedMappings.some(m => m.attribute_name === h.attribute))
                              .map(({ attribute, hint }) => (
                                <Alert key={attribute} severity="warning" sx={{ mb: 1, borderRadius: 2, alignItems: 'center' }}>
                                  <strong>{attribute}:</strong> {hint}
                                </Alert>
                              ))}
                          </Box>
                        )}
                        {posIdDupeWarning && (
                          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, alignItems: 'center' }}>
                            {posIdDupeWarning}
                          </Alert>
                        )}

                        {/* Table */}
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                          <Box sx={{
                            display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 100px',
                            bgcolor: 'grey.100', p: 2, borderBottom: '1px solid', borderColor: 'divider'
                          }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Excel Column</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Sample Value</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Bud-i Attribute</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', textAlign: 'center' }}>Confidence</Typography>
                          </Box>

                          <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                            {confirmedMappings.map((mapping, idx) => {
                              const sample = sampleData[0]?.[mapping.excel_col] ?? "—";
                              const conf = mapping.confidence ?? 0;
                              const showConfidence = !isEditMode || !!manualFile;
                              const dotColor = showConfidence ? (conf >= 0.85 ? '#4caf50' : conf >= 0.6 ? '#ff9800' : '#f44336') : '#e0e0e0';

                              return (
                                <Box key={mapping.excel_col} sx={{
                                  display: 'grid', gridTemplateColumns: '2fr 1fr 2fr 100px', gap: 2, alignItems: 'center', p: 2,
                                  borderBottom: idx < confirmedMappings.length - 1 ? '1px solid' : 'none',
                                  borderColor: 'divider',
                                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) }
                                }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{mapping.excel_col}</Typography>
                                  <Tooltip title={String(sample)}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {String(sample)}
                                    </Typography>
                                  </Tooltip>

                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={mapping.attribute_name || ""}
                                      onChange={(e) => handleExcelMappingChange(mapping.excel_col, e.target.value || null)}
                                      displayEmpty
                                      disabled={shopifyAutoFields.includes(mapping.attribute_name)}
                                      sx={{ borderRadius: 1.5, bgcolor: shopifyAutoFields.includes(mapping.attribute_name) ? 'action.disabledBackground' : 'background.paper', '& .MuiSelect-select': { py: 1 } }}
                                    >
                                      <MenuItem value=""><Typography variant="body2" color="text.secondary"><em>— Skip / Unmapped —</em></Typography></MenuItem>
                                      {targetAttributes
                                        .filter(attr => !shopifyAutoFields.includes(attr.attribute_name))
                                        .map(attr => {
                                          const isUsedElsewhere = confirmedMappings.some(m => m.excel_col !== mapping.excel_col && m.attribute_name === attr.attribute_name);
                                          return (
                                            <MenuItem key={attr.product_attribute_master_id} value={attr.attribute_name} disabled={isUsedElsewhere}>
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', opacity: isUsedElsewhere ? 0.5 : 1 }}>
                                                <Typography variant="body2">{attr.attribute_name} {attr.required && <span style={{ color: theme.palette.error.main }}>*</span>}</Typography>
                                                {isUsedElsewhere && <CheckCircleOutlineIcon fontSize="small" color="action" />}
                                              </Box>
                                            </MenuItem>
                                          );
                                        })}
                                    </Select>
                                  </FormControl>

                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: mapping.attribute_name && showConfidence ? dotColor : '#e0e0e0', boxShadow: mapping.attribute_name && showConfidence ? `0 0 8px ${alpha(dotColor, 0.5)}` : 'none' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: mapping.attribute_name && showConfidence ? 'text.primary' : 'text.disabled' }}>
                                      {mapping.attribute_name && showConfidence ? `${Math.round(conf * 100)}%` : "—"}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Paper>

                        {/* Non-WP/Shopify edit mode: note if no new file was uploaded */}
                        {isEditMode && !manualFile && (
                          <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 2, border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.2), display: 'flex', gap: 1.5, alignItems: 'center' }}>
                            <InfoOutlinedIcon color="warning" sx={{ flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary">Showing saved mappings from database. <strong>Go back and upload a new file</strong> if you need to add new columns to your mapping.</Typography>
                          </Box>
                        )}

                        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button
                              variant="outlined"
                              size="large"
                              onClick={() => setActiveStep(1)}
                              sx={{ py: 1.5, px: 4, borderRadius: 2, fontWeight: 700 }}
                            >
                              ← Back to Upload
                            </Button>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              {isEditMode && (
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  size="large"
                                  onClick={handleSaveWpMappings}
                                  disabled={loading}
                                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineIcon />}
                                  sx={{ py: 1.5, px: 4, borderRadius: 2, fontWeight: 700 }}
                                >
                                  Save Mappings
                                </Button>
                              )}
                              <Button
                                variant="contained"
                                size="large"
                                onClick={() => handleNextToFilters()}
                                disabled={loading || !!posIdDupeWarning || requiredMissing.some(req => !confirmedMappings.find(m => m.attribute_name === req)) || isGeneratingIcons}
                                startIcon={isGeneratingIcons ? <CircularProgress size={18} color="inherit" /> : null}
                                sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700 }}
                              >
                                {isGeneratingIcons ? "Analyzing Data..." : "Next: Customize Filters →"}
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Fade>
              )}

              {/* STEP 3: Chatbot Filter Configurations */}
              {activeStep === 3 && (
                <Fade in={true} timeout={500}>
                  <Box sx={{ position: 'relative', pt: 2, pb: 4 }}>
                    {isGeneratingIcons ? (
                      <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        minHeight: 500, bgcolor: 'background.paper', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                      }}>
                        <CircularProgress size={60} sx={{ mb: 3 }} />
                        <Typography variant="h5" fontWeight="700" sx={{ mb: 1 }}>Analyzing your store data...</Typography>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Chatbot Filter Configurations</Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                              Configure visibility, icons, and ranges for the Chatbot filters. Need icons? <a href="https://heroicons.com/" target="_blank" rel="noreferrer" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>Find SVG icons Here</a>.
                            </Typography>
                          </Box>
                          {isEditMode && (
                            (formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce") ? (
                              // WP edit mode: Re-Analyze calls live WP API
                              <Button
                                variant="outlined"
                                color="info"
                                onClick={handleReanalyzeFilters}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SettingsSuggestIcon />}
                                sx={{ borderRadius: 2, fontWeight: 600, flexShrink: 0 }}
                              >
                                Re-Analyze
                              </Button>
                            ) : Object.keys(colStats).length > 0 ? (
                              // Non-WP edit mode: Re-Analyze from uploaded file's colStats
                              <Button
                                variant="outlined"
                                color="info"
                                onClick={handleReanalyzeFiltersNonWp}
                                disabled={loading}
                                startIcon={<SettingsSuggestIcon />}
                                sx={{ borderRadius: 2, fontWeight: 600, flexShrink: 0 }}
                              >
                                Re-Analyze from File
                              </Button>
                            ) : (
                              // Non-WP edit mode: no file uploaded, Re-Analyze disabled
                              <Tooltip title="Upload a new file in Step 2 to enable re-analysis">
                                <span>
                                  <Button
                                    variant="outlined"
                                    color="info"
                                    disabled
                                    startIcon={<SettingsSuggestIcon />}
                                    sx={{ borderRadius: 2, fontWeight: 600, flexShrink: 0 }}
                                  >
                                    Re-Analyze
                                  </Button>
                                </span>
                              </Tooltip>
                            )
                          )}
                        </Box>

                        <Box sx={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'all 0.3s ease' }}>
                          {/* SECTION: CATEGORIES */}
                          <Box sx={{ mb: 6 }}>
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>Product Categories</Typography>
                              <Typography variant="body2" color="text.secondary">Select up to 9 categories to display.</Typography>
                            </Box>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Category</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Icon SVG Code</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Preview</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Visibility (Max 9)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {allCategories.map((cat, idx) => {
                                    const conf = categoryConfig[cat];
                                    const isVisible = conf?.is_visible || false;
                                    return (
                                      <TableRow key={cat} hover sx={{ opacity: isVisible ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                                        <TableCell sx={{ fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>{cat}</TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            spellCheck="false"
                                            value={conf?.icon_svg || ""}
                                            onChange={(e) => handleCategoryConfigChange(cat, 'icon_svg', null, e.target.value)}
                                            placeholder="<svg>...</svg>"
                                            disabled={!isVisible}
                                            inputProps={{ maxLength: 2000 }}
                                            sx={{ '& input': { fontSize: '0.85rem', fontFamily: 'monospace', color: 'text.secondary' } }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                                          <Box sx={{ width: 32, height: 32, mx: 'auto', opacity: isVisible ? 1 : 0.4, '& svg': { width: '100%', height: '100%', color: 'text.secondary' } }} dangerouslySetInnerHTML={{ __html: conf?.icon_svg || "" }} />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                                          <Switch
                                            checked={isVisible}
                                            onChange={(e) => handleCategoryConfigChange(cat, 'is_visible', null, e.target.checked)}
                                            color="primary"
                                          />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>

                          <Divider sx={{ my: 6, borderColor: alpha(theme.palette.divider, 0.8), borderStyle: 'dashed' }} />

                          {/* SECTION: MOODS */}
                          <Box sx={{ mb: 6 }}>
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>Mood Configuration</Typography>
                              <Typography variant="body2" color="text.secondary">Select up to 12 moods to display, and assign them to your active categories.</Typography>
                            </Box>
                            <TableContainer sx={{ mb: 4 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Mood</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Icon SVG Code</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Preview</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Visibility (Max 12)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {allMoods.map((mood) => {
                                    const conf = moodConfig[mood];
                                    const isVisible = conf?.is_visible || false;
                                    return (
                                      <TableRow key={mood} hover sx={{ opacity: isVisible ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                                        <TableCell sx={{ fontWeight: 600, textTransform: 'capitalize', borderBottom: '1px solid', borderColor: 'divider' }}>{mood}</TableCell>
                                        <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            spellCheck="false"
                                            value={conf?.icon_svg || ""}
                                            onChange={(e) => handleMoodConfigChange(mood, 'icon_svg', e.target.value)}
                                            placeholder="<svg>...</svg>"
                                            disabled={!isVisible}
                                            inputProps={{ maxLength: 2000 }}
                                            sx={{ '& input': { fontSize: '0.85rem', fontFamily: 'monospace', color: 'text.secondary' } }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                                          <Box sx={{ width: 32, height: 32, mx: 'auto', opacity: isVisible ? 1 : 0.4, '& svg': { width: '100%', height: '100%', color: 'text.secondary' } }} dangerouslySetInnerHTML={{ __html: conf?.icon_svg || "" }} />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                                          <Switch
                                            checked={isVisible}
                                            onChange={(e) => handleMoodConfigChange(mood, 'is_visible', e.target.checked)}
                                            color="primary"
                                          />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>

                            <Box sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.02), border: '1px dashed', borderColor: 'divider' }}>
                              <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, mb: 3 }}>
                                Mood Assignment
                              </Typography>

                              <Box sx={{ mb: 4 }}>
                                <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>Currently Active Moods:</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {allMoods.filter(m => moodConfig[m]?.is_visible).length > 0 ? (
                                    allMoods.filter(m => moodConfig[m]?.is_visible).map(m => (
                                      <Chip key={m} label={m} size="small" sx={{ fontWeight: 600, textTransform: 'capitalize', bgcolor: '#ffffff', border: '1px solid', borderColor: 'divider' }} />
                                    ))
                                  ) : (
                                    <Typography variant="body2" color="text.disabled">None selected</Typography>
                                  )}
                                </Box>
                              </Box>

                              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>Enable mood selection for these categories:</Typography>
                              <Grid container spacing={2}>
                                {allCategories.filter(c => categoryConfig[c]?.is_visible).map(cat => (
                                  <Grid item xs={12} sm={6} md={4} key={cat}>
                                    <Box
                                      sx={{
                                        px: 2, py: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        bgcolor: '#ffffff',
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: categoryConfig[cat]?.ask_mood ? 'primary.main' : 'divider',
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        boxShadow: categoryConfig[cat]?.ask_mood ? `0 0 0 1px ${theme.palette.primary.main}` : 'none'
                                      }}
                                      onClick={() => handleCategoryConfigChange(cat, 'ask_mood', null, !categoryConfig[cat]?.ask_mood)}
                                    >
                                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: categoryConfig[cat]?.ask_mood ? 'primary.main' : 'text.primary' }}>{cat}</Typography>
                                      <Switch
                                        checked={categoryConfig[cat]?.ask_mood || false}
                                        onChange={(e) => handleCategoryConfigChange(cat, 'ask_mood', null, e.target.checked)}
                                        onClick={(e) => e.stopPropagation()}
                                        color="primary"
                                        size="small"
                                      />
                                    </Box>
                                  </Grid>
                                ))}
                                {allCategories.filter(c => categoryConfig[c]?.is_visible).length === 0 && (
                                  <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">Please turn on visibility for at least one category above.</Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 6, borderColor: alpha(theme.palette.divider, 0.8), borderStyle: 'dashed' }} />

                          {/* SECTION: BUDGETS */}
                          <Box sx={{ mb: 6 }}>
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>Budget Tiers</Typography>
                              <Typography variant="body2" color="text.secondary">Define the global budget options shown to the user.</Typography>
                            </Box>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Display Text</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>From ($)</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>To ($)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {budgetRanges.map((br, idx) => (
                                    <TableRow key={idx} hover>
                                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}><TextField size="small" fullWidth value={br.display_text} onChange={(e) => handleBudgetChange(idx, 'display_text', e.target.value)} inputProps={{ maxLength: 50 }} /></TableCell>
                                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}><TextField type="number" size="small" fullWidth value={br.min} onChange={(e) => handleBudgetChange(idx, 'min', e.target.value)} inputProps={{ max: 10000 }} /></TableCell>
                                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}><TextField type="number" size="small" fullWidth value={br.max} onChange={(e) => handleBudgetChange(idx, 'max', e.target.value)} inputProps={{ max: 10000 }} /></TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>

                          <Divider sx={{ my: 6, borderColor: alpha(theme.palette.divider, 0.8), borderStyle: 'dashed' }} />

                          {/* SECTION: POTENCY */}
                          <Box sx={{ mb: 6 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>Potency Configuration</Typography>
                                <Typography variant="body2" color="text.secondary">Configure min/max thresholds for low, medium, and high potency.</Typography>
                              </Box>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={potencyFilterEnabled}
                                    onChange={(e) => {
                                      const hasThcMapping = (formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce")
                                        ? wpMappings.some(m => m.attribute_name === "thc_content_pct" || m.attribute_name === "thc_content_mg")
                                        : confirmedMappings.some(m => m.attribute_name === "thc_content_pct" || m.attribute_name === "thc_content_mg");

                                      if (e.target.checked && !hasThcMapping) {
                                        setSnackbar({ open: true, message: "Please map THC attributes in the previous step to enable potency filters.", severity: "warning" });
                                        return;
                                      }
                                      setPotencyFilterEnabled(e.target.checked);
                                    }}
                                    color="primary"
                                  />
                                }
                                label={<Typography sx={{ fontWeight: 600, color: potencyFilterEnabled ? 'primary.main' : 'text.secondary' }}>{potencyFilterEnabled ? "Enabled" : "Disabled"}</Typography>}
                                labelPlacement="start"
                                sx={{ mr: 0, bgcolor: alpha(theme.palette.primary.main, 0.04), pr: 2, pl: 1, py: 0.5, borderRadius: 2 }}
                              />
                            </Box>

                            {(() => {
                              const hasThcMapping = (formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce")
                                ? wpMappings.some(m => m.attribute_name === "thc_content_pct" || m.attribute_name === "thc_content_mg")
                                : confirmedMappings.some(m => m.attribute_name === "thc_content_pct" || m.attribute_name === "thc_content_mg");

                              if (!hasThcMapping && potencyFilterEnabled) {
                                return (
                                  <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                                    No THC attributes were mapped in the previous step. Potency filters will not work correctly until you map your store's THC fields to <strong>thc_content_pct</strong> or <strong>thc_content_mg</strong>.
                                  </Alert>
                                );
                              }
                              return null;
                            })()}

                            {potencyFilterEnabled && (
                              <Fade in={potencyFilterEnabled}>
                                <Box>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Category</TableCell>
                                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Enable?</TableCell>
                                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Unit</TableCell>
                                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Low (Min - Max)</TableCell>
                                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Medium (Min - Max)</TableCell>
                                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>High (Min - Max)</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {allCategories.filter(c => categoryConfig[c]?.is_visible).length === 0 ? (
                                          <TableRow>
                                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, border: 'none' }}><Typography variant="body2" color="text.disabled">No visible categories selected.</Typography></TableCell>
                                          </TableRow>
                                        ) : (
                                          allCategories.filter(c => categoryConfig[c]?.is_visible).map((cat) => {
                                            const conf = categoryConfig[cat];
                                            const isEnabled = conf?.ask_potency;
                                            return (
                                              <TableRow key={cat} hover sx={{ opacity: isEnabled ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                                                <TableCell sx={{ fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>{cat}</TableCell>
                                                <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                  <Switch checked={isEnabled} onChange={(e) => handleCategoryConfigChange(cat, 'ask_potency', null, e.target.checked)} color="success" size="small" />
                                                </TableCell>
                                                <TableCell sx={{ opacity: isEnabled ? 1 : 0.4, pointerEvents: isEnabled ? 'auto' : 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                  <ToggleButtonGroup
                                                    value={conf?.potency_unit}
                                                    exclusive
                                                    onChange={(e, newType) => { if (newType) handleCategoryConfigChange(cat, 'potency_unit', null, newType); }}
                                                    size="small"
                                                    sx={{ height: 32 }}
                                                  >
                                                    <ToggleButton value="pct" sx={{ px: 1.5, py: 0, textTransform: 'none', fontWeight: 600 }}>%</ToggleButton>
                                                    <ToggleButton value="mg" sx={{ px: 1.5, py: 0, textTransform: 'none', fontWeight: 600 }}>mg</ToggleButton>
                                                  </ToggleButtonGroup>
                                                </TableCell>
                                                <TableCell sx={{ opacity: isEnabled ? 1 : 0.4, pointerEvents: isEnabled ? 'auto' : 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                  <DualRangeInput minVal={conf?.low.min} maxVal={conf?.low.max} onMinChange={(val) => handleCategoryConfigChange(cat, 'low', 'min', val)} onMaxChange={(val) => handleCategoryConfigChange(cat, 'low', 'max', val)} disabled={!isEnabled} />
                                                </TableCell>
                                                <TableCell sx={{ opacity: isEnabled ? 1 : 0.4, pointerEvents: isEnabled ? 'auto' : 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                  <DualRangeInput minVal={conf?.medium.min} maxVal={conf?.medium.max} onMinChange={(val) => handleCategoryConfigChange(cat, 'medium', 'min', val)} onMaxChange={(val) => handleCategoryConfigChange(cat, 'medium', 'max', val)} disabled={!isEnabled} />
                                                </TableCell>
                                                <TableCell sx={{ opacity: isEnabled ? 1 : 0.4, pointerEvents: isEnabled ? 'auto' : 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                  <DualRangeInput minVal={conf?.high.min} maxVal={conf?.high.max} onMinChange={(val) => handleCategoryConfigChange(cat, 'high', 'min', val)} onMaxChange={(val) => handleCategoryConfigChange(cat, 'high', 'max', val)} disabled={!isEnabled} />
                                                </TableCell>
                                              </TableRow>
                                            );
                                          })
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              </Fade>
                            )}
                          </Box>

                          {/* SECTION: LOCATION FILTER */}
                          {(() => {
                            const isWp = formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce";
                            const hasLocationMapped = isWp
                              ? wpMappings.some(m => m.attribute_name === "location")
                              : confirmedMappings.some(m => m.attribute_name === "location");
                            return (
                              <Box sx={{ mb: 6 }}>
                                <Divider sx={{ my: 6, borderColor: alpha(theme.palette.divider, 0.8), borderStyle: 'dashed' }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                  <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>Location Filter</Typography>
                                    <Typography variant="body2" color="text.secondary">Allow the chatbot to filter products by store location.</Typography>
                                  </Box>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={locationFilterEnabled}
                                        onChange={(e) => {
                                          if (e.target.checked && !hasLocationMapped) {
                                            setSnackbar({ open: true, message: "Please map the Location attribute in the previous step to enable location filtering.", severity: "warning" });
                                            return;
                                          }
                                          setLocationFilterEnabled(e.target.checked);
                                        }}
                                        color="primary"
                                      />
                                    }
                                    label={<Typography sx={{ fontWeight: 600, color: locationFilterEnabled ? 'primary.main' : 'text.secondary' }}>{locationFilterEnabled ? "Enabled" : "Disabled"}</Typography>}
                                    labelPlacement="start"
                                    sx={{ mr: 0, bgcolor: alpha(theme.palette.primary.main, 0.04), pr: 2, pl: 1, py: 0.5, borderRadius: 2 }}
                                  />
                                </Box>



                                {hasLocationMapped && locationFilterEnabled && (
                                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    Location filtering is enabled. Products will be filterable by the <strong>location</strong> field you mapped in the previous step.
                                  </Alert>
                                )}
                              </Box>
                            );
                          })()}


                        </Box>
                        <Box sx={{ mt: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Button
                            variant="outlined"
                            size="large"
                            onClick={() => setActiveStep(2)}
                            disabled={loading}
                            sx={{ py: 1.5, px: 4, borderRadius: 2, fontWeight: 700 }}
                          >
                            ← Back to Mappings
                          </Button>
                          {isEditMode ? (
                            <Button
                              variant="contained"
                              color="success"
                              size="large"
                              onClick={handleSaveFiltersOnly}
                              disabled={loading}
                              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleOutlineIcon />}
                              sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700, boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
                            >
                              {loading ? "Saving..." : "Save Filters"}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              size="large"
                              onClick={handleManualUpload}
                              disabled={loading}
                              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AssignmentTurnedInIcon />}
                              sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700, boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
                            >
                              {loading ? "Processing..." : "Confirm & Start Sync"}
                            </Button>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </Fade>
              )}
            </Card>
          </>)}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} sx={{ width: "100%", borderRadius: 2, boxShadow: 3 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Location Limit Exceeded Dialog */}
        <Dialog open={locationLimitDialog.open} onClose={() => {}} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
            <ErrorOutlineIcon /> Location Limit Exceeded
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2, color: 'text.primary' }}>
              Your current subscription allows for a maximum of <strong>{locationLimitDialog.allowed}</strong> store location(s).
              However, we found <strong>{locationLimitDialog.found}</strong> distinct locations in your data.
            </DialogContentText>
            
            <Paper variant="outlined" sx={{ p: 2, mb: 3, maxHeight: 150, overflow: 'auto', bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Detected Locations:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {locationLimitDialog.locations.map((loc, i) => (
                  <Chip key={i} label={loc} size="small" />
                ))}
              </Box>
            </Paper>

            <DialogContentText variant="body2" color="text.secondary">
              <strong>Option 1:</strong> Edit your store configuration to add support for more locations.<br/>
              <strong>Option 2:</strong> Continue without location mapping (the location filter will be disabled).
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => {
                // Unmap location
                const isWp = formData.posSystem?.trim().toLowerCase() === "wordpress" || formData.posSystem?.trim().toLowerCase() === "woocommerce";
                if (isWp) {
                  setWpMappings(prev => prev.map(m => m.attribute_name === "location" ? { ...m, attribute_name: null } : m));
                } else {
                  setConfirmedMappings(prev => prev.map(m => m.attribute_name === "location" ? { ...m, attribute_name: null } : m));
                }
                // Close dialog and instantly move to step 3 so the loading screen appears
                setLocationLimitDialog(prev => ({ ...prev, open: false }));
                setActiveStep(3);
                // Proceed to fetch filters and icons
                handleNextToFilters(true);
              }}
              disabled={isGeneratingIcons}
            >
              {isGeneratingIcons ? <CircularProgress size={24} color="inherit" /> : "Continue Without Location"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                // Navigate to subscription management page
                navigate(`/store-management/${formData.selectedStoreId}/edit-store`);
              }}
            >
              Edit / Add Location
            </Button>
          </DialogActions>
        </Dialog>
      </Box >
    );
  };
  export default POSConfigure;
